import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  UpdateProfileRequestSchema,
  DEFAULT_LANGUAGE,
  type UpdateProfileRequest,
  type User
} from '@bliks-io/contracts';
import { Button } from '@bliks-io/ui';
import { FormSelect } from '@/components/form/rhf';
import { useUpdateProfile } from '@/api/mutations/useProfile';
import { useOrgSettings } from '@/api/queries/useOrgSettings';

interface PreferencesTabProps {
  user: User;
  onSuccess?: () => void;
}

/**
 * Preferences Tab Component
 *
 * Allows users to manage their application preferences:
 * - Language selection
 */
export function PreferencesTab({ user, onSuccess }: PreferencesTabProps) {
  const { t } = useTranslation();
  const updateProfile = useUpdateProfile();
  const { data: orgSettings } = useOrgSettings();
  const defaultLanguage = user.language || orgSettings?.defaultLanguage || DEFAULT_LANGUAGE;

  const {
    handleSubmit,
    control,
    formState: { isDirty, isSubmitting }
  } = useForm<UpdateProfileRequest>({
    resolver: zodResolver(UpdateProfileRequestSchema),
    values: { language: defaultLanguage }
  });

  const onSubmit = async (data: UpdateProfileRequest) => {
    await updateProfile.mutateAsync(data);
    onSuccess?.();
  };

  const languageOptions = [
    {
      value: 'en',
      label: 'English'
    },
    {
      value: 'de',
      label: 'Deutsch'
    },
    {
      value: 'hu',
      label: 'Magyar'
    },
    {
      value: 'fr',
      label: 'Français'
    }
  ];

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <FormSelect
            control={control}
            name="language"
            label={t('userSettings.preferences.language')}
            placeholder={t('userSettings.preferences.language')}
            options={languageOptions}
            required
          />
          <p className="text-xs text-muted-foreground">
            {t('userSettings.preferences.languageHelper')}
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <Button type="submit" loading={isSubmitting || updateProfile.isPending} disabled={!isDirty}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
